import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import MapEngine from "../components/map/MapEngine";
import MapCollectible from "../components/map/MapCollectible";
import { businessMarkerEl, meMarkerEl, comuneMarkerEl, categoryMarkerEl, comuneHubEl, riderMarkerEl, eventMarkerEl, EVENT_CAT_LABELS } from "../components/map/markers";
import { DEMO_SPONSORS } from "../components/map/sponsors";
import MapStatStrip from "../components/MapStatStrip";
import { Palette, Sparkles } from "lucide-react";
import { HomeModeToggle } from "../components/smart/HomeModeToggle";
import { PERF, scaleGhosts, applyPerfClass, startFpsGuard } from "../lib/perf";
import { COMUNI, TERRITORY_CENTER, TERRITORY_ZOOM, zoneToComuneId } from "../components/map/comuni";
import { CategoryGlyph, CATEGORY_LABELS, categoryKey } from "../components/map/categoryIcons";
import { Input } from "../components/ui/input";
import {
  Navigation, Crosshair, Bell, Trophy, Gift, Gem, Sun, Star, X, Search, ChevronLeft, Bike, Target,
  Map as MapIcon, ClipboardList, ShoppingCart, User, Briefcase, CalendarDays, MapPin, Megaphone, Radio,
  Home, Compass,
} from "lucide-react";
import { toast } from "sonner";
import confetti from "canvas-confetti";

const TERRITORY_PITCH = 16;     // vista d'insieme quasi piatta: distanze uniformi, niente compressione
const DETAIL_PITCH = 56;        // vista comune/attività: prospettiva 3D da gioco
const CATEGORIES_ZOOM = 14.2;   // vista comune: categorie a raggiera
const ACTIVITIES_ZOOM = 15.6;   // vista attività di una categoria
const RING_RADIUS = 0.0044;     // raggio (in gradi) della raggiera categorie
const COMUNE_MIN_SEP = 0.012;   // separazione minima (gradi): piccola → i comuni restano sulle posizioni reali

// Rilassamento anti-sovrapposizione: mantiene le posizioni geografiche relative dei
// comuni ma li allontana finché nessuna coppia è più vicina di COMUNE_MIN_SEP.
// I "center" reali restano invariati (usati per la logica); si sposta solo `disp`.
function relaxComuni(comuni, minDeg) {
  const cos = Math.cos((45.22 * Math.PI) / 180);
  const pts = comuni.map((c) => ({ c, x: c.center[0], y: c.center[1] }));
  for (let iter = 0; iter < 120; iter++) {
    for (let i = 0; i < pts.length; i++) {
      for (let j = i + 1; j < pts.length; j++) {
        const dx = (pts[i].x - pts[j].x) * cos;
        const dy = pts[i].y - pts[j].y;
        const d = Math.hypot(dx, dy) || 1e-6;
        if (d < minDeg) {
          const push = (minDeg - d) / 2;
          const ux = dx / d, uy = dy / d;
          pts[i].x += (ux * push) / cos; pts[i].y += uy * push;
          pts[j].x -= (ux * push) / cos; pts[j].y -= uy * push;
        }
      }
    }
  }
  return pts.map((p) => ({ ...p.c, disp: [p.x, p.y] }));
}
const RELAXED_COMUNI = relaxComuni(COMUNI, COMUNE_MIN_SEP);

// Particelle ambientali (posizioni deterministiche → nessun re-render casuale).
const PARTICLES = Array.from({ length: 12 }, (_, i) => ({
  left: `${7 + ((i * 37) % 86)}%`, top: `${12 + ((i * 53) % 74)}%`,
  dur: `${5 + (i % 5)}s`, delay: `${(i * 0.5).toFixed(1)}s`,
}));

// Centro della vista territorio = centroide dei comuni ATTIVI (dopo il rilassamento),
// così il cluster è sempre inquadrato correttamente.
const ACTIVE_CENTROID = (() => {
  const act = RELAXED_COMUNI.filter((c) => c.active);
  const n = act.length || 1;
  return [act.reduce((s, c) => s + c.disp[0], 0) / n, act.reduce((s, c) => s + c.disp[1], 0) / n];
})();


// Disposizione radiale attorno a un centro geografico (partendo dall'alto, in senso orario).
function radialLayout(center, items, radiusDeg) {
  const [lng, lat] = center;
  const cos = Math.cos((lat * Math.PI) / 180) || 1;
  const n = Math.max(items.length, 1);
  return items.map((it, i) => {
    const ang = (i * 2 * Math.PI) / n;
    return { ...it, lat: lat + radiusDeg * Math.cos(ang), lng: lng + (radiusDeg / cos) * Math.sin(ang) };
  });
}

// Filtri "Comune Hub": categorie rapide per scegliere COSA fare una volta entrati nel territorio.
const HUB_FOOD_KW = ["ristor", "pizz", "bar", "gelat", "food", "kebab", "sushi", "panin", "forno", "macell", "gastronom", "poke", "hambur", "caff", "pasticc", "enotec", "braceria", "trattoria", "osteria"];
const HUB_SERVICE_KW = ["parrucch", "barbiere", "estetic", "benessere", "spa", "officina", "lavand", "servizi", "studio", "scuola", "palestra", "fisio", "tatuagg", "idraul", "elettr", "centro"];
function applyHubFilter(acts, f) {
  if (!f || f === "all") return acts;
  if (f === "evidenza") return acts.filter((p) => p.tier === "premium" || p.tier === "sponsor");
  if (f === "aperti") return acts.filter((p) => p.orderable);
  const kw = f === "mangiare" ? HUB_FOOD_KW : f === "servizi" ? HUB_SERVICE_KW : [];
  if (!kw.length) return acts;
  return acts.filter((p) => { const c = `${p.category || ""}`.toLowerCase(); return kw.some((k) => c.includes(k)); });
}

// Evita marker sovrapposti (attività nello stesso edificio / molto vicine): relaxation
// iterativa che allontana OGNI coppia più vicina di minDeg, preservando la posizione
// geografica relativa. Corregge in modo robusto anche cluster di 3+ marker (l'algoritmo
// precedente risolveva solo la prima coppia, lasciando le altre sovrapposte).
function spreadOverlaps(items, minDeg = 0.00068) {
  const cos = Math.cos((45.22 * Math.PI) / 180);
  const out = items.map((p) => ({ ...p }));
  for (let iter = 0; iter < 60; iter++) {
    let moved = false;
    for (let i = 0; i < out.length; i++) {
      for (let j = i + 1; j < out.length; j++) {
        const dx = (out[i].lng - out[j].lng) * cos;
        const dy = out[i].lat - out[j].lat;
        let d = Math.hypot(dx, dy);
        if (d >= minDeg) continue;
        moved = true;
        if (d < 1e-9) {
          // Sovrapposizione esatta (stesse coordinate): sposta i su un anello deterministico.
          const ang = (i * 2 * Math.PI) / out.length;
          out[i].lng += (Math.cos(ang) * minDeg) / cos;
          out[i].lat += Math.sin(ang) * minDeg;
          continue;
        }
        const push = (minDeg - d) / 2;
        const ux = dx / d, uy = dy / d;
        out[i].lng += (ux * push) / cos; out[i].lat += uy * push;
        out[j].lng -= (ux * push) / cos; out[j].lat -= uy * push;
      }
    }
    if (!moved) break;
  }
  return out;
}

// Token comune per il matching con l'Activity Wishlist (accenti/punteggiatura rimossi).
function comuneToken(s) {
  return (s || "").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z]+/g, " ").trim().split(" ")[0] || "";
}

export default function Explore() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { count, subtotal } = useCart();
  const [pois, setPois] = useState([]);
  const [stats, setStats] = useState({ deliveries_today: 0, active_places: 0 });
  const [weather, setWeather] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [focus, setFocus] = useState(null);
  const [me, setMe] = useState(null);
  const [flash, setFlash] = useState(false);
  const [level, setLevel] = useState(null);
  const [liveRiders, setLiveRiders] = useState([]);
  const [activeDeliveries, setActiveDeliveries] = useState(0);
  const [burst, setBurst] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventsOpen, setEventsOpen] = useState(false);
  const [ghostCount, setGhostCount] = useState(0);
  const [ghostRoutes, setGhostRoutes] = useState([]);
  const [liveFeed, setLiveFeed] = useState(null);
  const [liveOpen, setLiveOpen] = useState(false);
  const [comuniProgress, setComuniProgress] = useState({ threshold: 0, by_token: {} });
  const [energyCard, setEnergyCard] = useState(null);
  const [plan, setPlan] = useState(null);
  const [radarPois, setRadarPois] = useState([]);
  const [radarSel, setRadarSel] = useState(null);
  const [myReq, setMyReq] = useState([]);
  const requestRadar = async (rid) => {
    try {
      const { data } = await api.post(`/radar/request/${rid}`, {});
      setMyReq((m) => [...new Set([...m, rid])]);
      setRadarPois((ps) => ps.map((p) => (p.id === rid ? { ...p, requests: data.count } : p)));
      setRadarSel((s) => (s ? { ...s, requests: data.count } : s));
      toast.success(data.already ? "L'hai già richiesta 💚" : "Grazie! Richiesta registrata 💚");
    } catch (e) { toast("Accedi per richiedere un'attività su Fuma"); }
  };

  // Navigazione a 2 livelli: territorio -> attività del comune (In evidenza + lista completa)
  const [view, setView] = useState("territory");
  const [activeComune, setActiveComune] = useState(null);
  const [listOpen, setListOpen] = useState(false);
  const [discover, setDiscover] = useState(null);

  // Performance-first: applica la classe di qualità (riduce effetti su device deboli) + guard FPS.
  const [perfTier, setPerfTier] = useState(PERF.tier);
  useEffect(() => { applyPerfClass(); startFpsGuard((t) => setPerfTier(t)); }, []);
  const [theme, setTheme] = useState(() => localStorage.getItem("fuma_theme") || "world");
  useEffect(() => { localStorage.setItem("fuma_theme", theme); }, [theme]);

  // Apre il pannello Canavese Live se richiesto dalla Home Smart (?panel=live).
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("panel") === "live") setLiveOpen(true);
  }, []);

  // Piano Fuma AI: si entra dalla Home AI con ?plan=1 → tappe numerate + percorso sulla mappa.
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("plan") !== "1") return;
    try {
      const raw = sessionStorage.getItem("fuma_active_plan");
      if (!raw) return;
      const pl = JSON.parse(raw)?.proposal;
      if (!pl?.steps?.length) return;
      setPlan(pl);
      const pts = pl.steps.filter((s) => s.lat != null && s.lng != null);
      if (pts.length) {
        const clat = pts.reduce((a, p) => a + p.lat, 0) / pts.length;
        const clng = pts.reduce((a, p) => a + p.lng, 0) / pts.length;
        setView("territory"); setActiveComune(null);
        setFocus({ lat: clat, lng: clng, zoom: 13.2, pitch: TERRITORY_PITCH });
      }
    } catch (e) { /* piano non valido */ }
  }, []);

  // Aggiornamenti dinamici SENZA reload (polling intelligente, in pausa se la tab è nascosta).
  useEffect(() => {
    const loadMap = () => { api.get("/public/map").then(({ data }) => { setPois(data.pois || []); setStats(data.stats || {}); }).catch(() => {}); api.get("/radar/public").then(({ data }) => setRadarPois(data.pois || [])).catch(() => {}); api.get("/radar/my-requests").then(({ data }) => setMyReq(data.ids || [])).catch(() => {}); };
    const loadLive = () => api.get("/public/live").then(({ data }) => { setLiveRiders(data.riders || []); setActiveDeliveries(data.active_deliveries || 0); setGhostCount(scaleGhosts(data.ghosts || 0)); }).catch(() => {});
    const loadEvents = () => api.get("/public/events").then(({ data }) => setEvents(Array.isArray(data) ? data : [])).catch(() => {});
    const loadFeed = () => api.get("/public/live-feed").then(({ data }) => setLiveFeed(data)).catch(() => {});
    loadMap(); loadLive(); loadEvents(); loadFeed();
    fetch("https://api.open-meteo.com/v1/forecast?latitude=45.216&longitude=7.628&current=temperature_2m,weather_code,is_day")
      .then((r) => r.json()).then((d) => setWeather({ temp: Math.round(d?.current?.temperature_2m), code: d?.current?.weather_code, isDay: d?.current?.is_day === 1 })).catch(() => {});
    const tMap = setInterval(() => { if (!document.hidden) loadMap(); }, 30000);
    const tLive = setInterval(() => { if (!document.hidden) loadLive(); }, 12000);
    const tEvents = setInterval(() => { if (!document.hidden) loadEvents(); }, 60000);
    const tFeed = setInterval(() => { if (!document.hidden) loadFeed(); }, 45000);
    return () => { clearInterval(tMap); clearInterval(tLive); clearInterval(tEvents); clearInterval(tFeed); };
  }, []);

  useEffect(() => {
    if (!user) { setLevel(null); return; }
    api.get("/user/level").then(({ data }) => setLevel(data)).catch(() => {});
  }, [user]);

  // Percorsi stradali reali (OSRM, via backend cache) per far seguire le STRADE ai ghost.
  useEffect(() => {
    if (!ghostCount) { setGhostRoutes([]); return; }
    let stop = false;
    const load = () => api.get(`/public/ghost-routes?n=${ghostCount}`).then(({ data }) => { if (!stop) setGhostRoutes(data.routes || []); }).catch(() => {});
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 600000);
    return () => { stop = true; clearInterval(t); };
  }, [ghostCount]);

  // Feedback ricompense (XP/crediti/badge) direttamente sulla mappa + coriandoli.
  useEffect(() => {
    if (!user) return;
    let stop = false;
    const check = () => {
      api.get("/user/rewards/unseen").then(({ data }) => {
        if (stop || !data || !data.length) return;
        const xp = data.reduce((s, r) => s + (r.reward?.xp || 0), 0);
        const credits = data.reduce((s, r) => s + (r.reward?.credits || 0), 0);
        const badges = data.filter((r) => r.reward?.badge).length;
        setBurst({ xp, credits, badges, title: data[0].mission_title, tier: data[0].tier_label });
        confetti({ particleCount: 120, spread: 78, origin: { y: 0.4 }, colors: ["#00E676", "#FFD700", "#7CFFC4", "#C4B5FD"] });
        api.post("/user/rewards/seen").catch(() => {});
        api.get("/user/level").then(({ data }) => setLevel(data)).catch(() => {});
        setTimeout(() => { if (!stop) setBurst(null); }, 4500);
      }).catch(() => {});
    };
    check();
    const t = setInterval(() => { if (!document.hidden) check(); }, 25000);
    return () => { stop = true; clearInterval(t); };
  }, [user]);

  // Override amministrativi del centro comune ("Gestisci Mappa" CEO/Centrale) → riposizionano
  // apertura/energia/centering/aggregazioni. Le label native MapLibre restano intatte.
  const [centerOverrides, setCenterOverrides] = useState({});
  const [addedComuni, setAddedComuni] = useState([]);
  const COMUNI_EFF = useMemo(() => {
    const base = COMUNI.map((c) => {
      const o = centerOverrides[c.id];
      return o ? { ...c, center: [o.lng, o.lat] } : c;
    });
    const extra = (addedComuni || []).map((a) => ({ id: a.id, name: a.name, center: [a.lng, a.lat], active: a.active !== false }));
    return [...base, ...extra];
  }, [centerOverrides, addedComuni]);
  const RELAXED_EFF = useMemo(() => relaxComuni(COMUNI_EFF, COMUNE_MIN_SEP), [COMUNI_EFF]);
  const ACTIVE_CENTROID_EFF = useMemo(() => {
    const act = RELAXED_EFF.filter((c) => c.active);
    const n = act.length || 1;
    return [act.reduce((s, c) => s + c.disp[0], 0) / n, act.reduce((s, c) => s + c.disp[1], 0) / n];
  }, [RELAXED_EFF]);

  // Assegna ogni attività al comune attivo più vicino + statistiche per comune.
  const { comuneStats, poiComune } = useMemo(() => {
    const s = {}; const pc = {};
    COMUNI_EFF.forEach((c) => { s[c.id] = { count: 0, offer: false, partner: false, openNow: 0 }; });
    pois.forEach((p) => {
      // AUTORITATIVO: assegna al comune DICHIARATO (zone) se noto e attivo.
      let best = null;
      const zid = zoneToComuneId(p.zone);
      if (zid) best = COMUNI_EFF.find((c) => c.id === zid && c.active) || null;
      // Fallback: comune attivo più vicino per coordinate (solo se zona sconosciuta).
      if (!best && p.lat != null && p.lng != null) {
        let bd = Infinity;
        COMUNI_EFF.forEach((c) => {
          if (!c.active) return;
          const dlat = p.lat - c.center[1];
          const dlng = (p.lng - c.center[0]) * Math.cos((p.lat * Math.PI) / 180);
          const d = dlat * dlat + dlng * dlng;
          if (d < bd) { bd = d; best = c; }
        });
      }
      if (best) {
        s[best.id].count += 1;
        if (p.has_offer) s[best.id].offer = true;
        if (p.orderable) s[best.id].openNow += 1;
        if (p.tier === "premium" || p.tier === "sponsor" || p.is_sponsored) s[best.id].partner = true;
        pc[p.id] = best.id;
      }
    });
    return { comuneStats: s, poiComune: pc };
  }, [pois, COMUNI_EFF]);

  // Attività "LIVE" (eventi in corso) per comune → il nome del comune si accende.
  const liveByComune = useMemo(() => {
    const m = {};
    (events || []).forEach((e) => {
      if (!e.is_live || e.lat == null || e.lng == null) return;
      let best = null, bd = Infinity;
      COMUNI_EFF.forEach((c) => {
        if (!c.active) return;
        const dlat = e.lat - c.center[1];
        const dlng = (e.lng - c.center[0]) * Math.cos((e.lat * Math.PI) / 180);
        const d = dlat * dlat + dlng * dlng;
        if (d < bd) { bd = d; best = c; }
      });
      if (best) m[best.id] = (m[best.id] || 0) + 1;
    });
    return m;
  }, [events, COMUNI_EFF]);

  // Eventi (tutti) per comune → alimenta l'intensità "Energia Fuma" e la mini-card.
  const eventsByComune = useMemo(() => {
    const m = {};
    (events || []).forEach((e) => {
      if (e.lat == null || e.lng == null) return;
      let best = null, bd = Infinity;
      COMUNI_EFF.forEach((c) => {
        if (!c.active) return;
        const dlat = e.lat - c.center[1];
        const dlng = (e.lng - c.center[0]) * Math.cos((e.lat * Math.PI) / 180);
        const d = dlat * dlat + dlng * dlng;
        if (d < bd) { bd = d; best = c; }
      });
      if (best) m[best.id] = (m[best.id] || 0) + 1;
    });
    return m;
  }, [events, COMUNI_EFF]);

  // Progresso di sblocco dei comuni bloccati (richieste Activity Wishlist → passaparola).
  useEffect(() => {
    let stop = false;
    api.get("/public/comuni-progress").then((r) => { if (!stop) setComuniProgress(r.data || { threshold: 0, by_token: {} }); }).catch(() => {});
    api.get("/public/comuni-centers").then((r) => { if (!stop) { setCenterOverrides(r.data?.overrides || {}); setAddedComuni(r.data?.added || []); } }).catch(() => {});
    return () => { stop = true; };
  }, []);

  // Esperienze per comune (da /public/map stats.experiences_by_zone → match per token).
  const experiencesByComune = useMemo(() => {
    const byZone = stats.experiences_by_zone || {};
    const m = {};
    COMUNI_EFF.forEach((c) => {
      const ct = comuneToken(c.name);
      let sum = 0;
      Object.entries(byZone).forEach(([z, n]) => { if (comuneToken(z) === ct) sum += n; });
      if (sum) m[c.id] = sum;
    });
    return m;
  }, [stats, COMUNI_EFF]);

  // Ordini live (consegne reali in corso) per comune → l'energia pulsa dove si ordina ora.
  const liveOrdersByComune = useMemo(() => {
    const m = {};
    (liveRiders || []).forEach((r) => {
      if (r.lat == null || r.lng == null) return;
      let best = null, bd = Infinity;
      COMUNI_EFF.forEach((c) => {
        if (!c.active) return;
        const dlat = r.lat - c.center[1];
        const dlng = (r.lng - c.center[0]) * Math.cos((r.lat * Math.PI) / 180);
        const d = dlat * dlat + dlng * dlng;
        if (d < bd) { bd = d; best = c; }
      });
      if (best) m[best.id] = (m[best.id] || 0) + 1;
    });
    return m;
  }, [liveRiders, COMUNI_EFF]);

  // Attività del comune selezionato (tutte). "In evidenza" = Premium/Sponsor (sulla mappa).
  const comuneActs = useMemo(() => {
    if (!activeComune) return [];
    return pois.filter((p) => poiComune[p.id] === activeComune.id);
  }, [activeComune, pois, poiComune]);

  // Marker "In evidenza": solo Premium/Sponsor, con de-overlap.
  const featuredActs = useMemo(() => {
    const feat = comuneActs.filter((p) => p.tier === "premium" || p.tier === "sponsor");
    return spreadOverlaps(feat).map((p, i) => ({ ...p, _i: i }));
  }, [comuneActs]);

  // Lista completa "Scopri tutte le attività" (Sponsor → Premium → Standard, aperti prima).
  const [hubFilter, setHubFilter] = useState("all");
  const listActs = useMemo(() => {
    const rank = { sponsor: 0, premium: 1, standard: 2 };
    let acts = comuneActs;
    if (q) acts = acts.filter((p) => (p.name || "").toLowerCase().includes(q.toLowerCase()));
    acts = applyHubFilter(acts, hubFilter);
    return [...acts].sort((a, b) => (rank[a.tier] ?? 2) - (rank[b.tier] ?? 2) || (b.orderable ? 1 : 0) - (a.orderable ? 1 : 0));
  }, [comuneActs, q, hubFilter]);
  const planItems = useMemo(() => (plan?.steps || []).filter((s) => s.lat != null && s.lng != null).map((s, i) => ({ ...s, id: `plan-${i}`, _n: i + 1 })), [plan]);
  const mapLines = useMemo(() => (planItems.length >= 2 ? [{ coords: planItems.map((s) => [s.lng, s.lat]) }] : []), [planItems]);

  const ghostSeeds = useMemo(() => {
    const s = (pois || []).filter((p) => p.lat != null && p.lng != null).map((p) => [p.lng, p.lat]);
    COMUNI_EFF.filter((c) => c.active).forEach((c) => s.push(c.center));
    return s;
  }, [pois, COMUNI_EFF]);

  const openPoi = (p) => { setSelected(p); setFocus({ lat: p.lat, lng: p.lng, zoom: 16 }); };

  // Barra assistente: suggerimenti cliccabili che aprono risultati filtrati / azioni.
  const SUGGESTIONS = [
    { id: "gelato", emoji: "🍦", label: "Hai voglia di un gelato?", type: "filter", match: ["gelat"] },
    { id: "pizza", emoji: "🍕", label: "Ordina una pizza", type: "filter", match: ["pizz"] },
    { id: "fiori", emoji: "🌸", label: "Sorprendi con dei fiori", type: "filter", match: ["fior", "flower", "vivaio"] },
    { id: "nuovo", emoji: "🍣", label: "Prova qualcosa di nuovo", type: "filter", match: ["sushi", "kebab", "etnic", "giappo", "poke", "messic"] },
    { id: "eventi", emoji: "🎉", label: "Scopri gli eventi di oggi", type: "events" },
    { id: "romantica", emoji: "❤️", label: "Organizza una serata romantica", type: "route", route: "/esperienze" },
  ];
  const handleSuggestion = (s) => {
    if (s.type === "events") return setEventsOpen(true);
    if (s.type === "route") return navigate(s.route);
    const kws = s.match || [];
    const items = pois.filter((p) => { const t = `${p.category || ""} ${p.name || ""}`.toLowerCase(); return kws.some((k) => t.includes(k)); });
    setDiscover({ label: s.label, items });
  };
  const openEvent = (e) => { setSelected(null); setEventsOpen(false); setSelectedEvent(e); if (e.lat != null && e.lng != null) setFocus({ lat: e.lat, lng: e.lng, zoom: 15.4 }); };
  const doFlash = () => { setFlash(true); setTimeout(() => setFlash(false), 420); };

  // Tap sull'Energia Fuma → mini-card del comune (dettagli solo ora, non sulla mappa).
  const openEnergyCard = (c) => setEnergyCard(c);
  // CTA "Esplora" / ingresso reale nel comune.
  const enterComune = (c) => {
    setEnergyCard(null);
    if (!c.active) { navigate("/app/regala"); return; }
    doFlash();
    setSelected(null); setQ(""); setHubFilter("all"); setActiveComune(c); setView("activities"); setListOpen(true);
    setFocus({ lat: c.center[1], lng: c.center[0], zoom: ACTIVITIES_ZOOM, pitch: DETAIL_PITCH });
  };
  const goBack = () => {
    doFlash();
    setSelected(null); setQ(""); setListOpen(false);
    setActiveComune(null); setView("territory");
    setFocus({ lat: ACTIVE_CENTROID_EFF[1], lng: ACTIVE_CENTROID_EFF[0], zoom: TERRITORY_ZOOM, pitch: TERRITORY_PITCH });
  };

  const layers = useMemo(() => {
    const l = [];
    if (view === "territory") {
      l.push({
        id: "comuni", anchor: "center", declutter: true,
        sig: (c) => `${c.id}${c.active}${c.intensity}${c.openNow}${c.liveOrders}${c.experiences}${c.partner}${c.live}`,
        items: RELAXED_EFF.map((c, i) => {
          const st = comuneStats[c.id] || {};
          const count = c.active ? (st.count || 0) : 0;
          const openNow = c.active ? (st.openNow || 0) : 0;
          const partner = c.active ? !!st.partner : false;
          const live = c.active ? (liveByComune[c.id] || 0) : 0;
          const evts = c.active ? (eventsByComune[c.id] || 0) : 0;
          const exps = c.active ? (experiencesByComune[c.id] || 0) : 0;
          const liveOrders = c.active ? (liveOrdersByComune[c.id] || 0) : 0;
          // Intensità "Energia Fuma" — data-driven (attività, aperte, eventi, esperienze, ordini live, premium).
          const score = count * 1 + openNow * 2 + evts * 1.5 + exps * 1 + liveOrders * 2.5 + live * 3 + (partner ? 2 : 0);
          const intensity = !c.active ? 0 : (score >= 10 ? 3 : score >= 4 ? 2 : (count > 0 ? 1 : 0));
          const priority = !c.active ? 0 : ((live > 0 || liveOrders > 0) ? 5 : partner ? 4 : intensity);
          // Zoom lontano/medio: aloni Fuma; oltre 13.8 lasciano spazio alle attività reali.
          const minZoom = c.active ? 0 : 11.5;
          const maxZoom = 13.8;
          let unlock = null;
          if (!c.active && comuniProgress.threshold > 0) {
            const requests = comuniProgress.by_token[comuneToken(c.name)] || 0;
            const threshold = comuniProgress.threshold;
            unlock = { requests, threshold, remaining: Math.max(0, threshold - requests), pct: Math.min(100, (requests / threshold) * 100) };
          }
          // Energia Fuma ancorata al CENTRO REALE del comune (coordinate native), NON al disp de-overlappato.
          return { id: c.id, lat: c.center[1], lng: c.center[0], ...c, count, openNow, liveOrders, partner, live, events: evts, experiences: exps, intensity, priority, minZoom, maxZoom, unlock, _i: i };
        }),
        build: (c) => comuneMarkerEl(c, openEnergyCard, false, c._i),
      });
    } else if (view === "activities" && activeComune) {
      l.push({ id: "hub", anchor: "bottom", items: [{ id: "hub", lat: activeComune.center[1], lng: activeComune.center[0], name: activeComune.name }], build: (c) => comuneHubEl(c) });
      l.push({
        id: "poi",
        sig: (p) => `${p.id}${p.lat}${p.lng}${p.has_offer}${p.tier}${p.name}${selected?.id === p.id}`,
        items: featuredActs,
        build: (poi) => businessMarkerEl(poi, openPoi, poi._i, selected?.id === poi.id),
      });
    }
    if (me) l.push({ id: "me", anchor: "center", items: [{ id: "me", lat: me.lat, lng: me.lng }], build: meMarkerEl });
    if (liveRiders.length) l.push({ id: "riders", anchor: "center", noCull: true, sig: (r) => r.id, items: liveRiders, build: (r) => riderMarkerEl(r.id) });
    if (events.length) l.push({ id: "events", anchor: "bottom", noCull: true, sig: (e) => `${e.id}${e.lat}${e.lng}${e.title}${e.is_live}${e.sponsored}${e.featured}`, items: events.filter((e) => e.lat != null && e.lng != null), build: (e) => eventMarkerEl(e, openEvent) });
    if (planItems.length) {
      l.push({
        id: "plan", anchor: "center", noCull: true, sig: (s) => `${s.id}${s.lat}${s.lng}`, items: planItems,
        build: (s) => { const el = document.createElement("div"); el.className = "fuma-plan-pin"; el.textContent = String(s._n); el.setAttribute("data-testid", `plan-pin-${s._n}`); el.onclick = () => { if (s.business_id) navigate(`/attivita/${s.business_id}`); }; return el; },
      });
    }
    if (activeComune && radarPois.length) {
      const c = activeComune.center;
      const near = radarPois.filter((p) => p.lat != null && Math.hypot((p.lng - c[0]) * Math.cos((c[1] * Math.PI) / 180), p.lat - c[1]) * 111 < 4);
      if (near.length) l.push({ id: "radar", anchor: "center", noCull: true, sig: (s) => `r${s._rid}${s.requests}`, items: near.map((p) => ({ ...p, id: `radar-${p.id}`, _rid: p.id })),
        build: (p) => { const el = document.createElement("div"); el.className = "radar-poi-pin"; el.setAttribute("data-testid", `radar-poi-${p._rid}`); el.title = p.name; el.onclick = () => setRadarSel(radarPois.find((x) => x.id === p._rid)); return el; } });
    }
    return l;
    // eslint-disable-next-line
  }, [view, activeComune, featuredActs, comuneStats, liveByComune, eventsByComune, experiencesByComune, liveOrdersByComune, comuniProgress, pois, me, selected, liveRiders, events, perfTier, planItems, radarPois]);

  // Segnala quando un pannello/comune è aperto così il FAB Assistente non si sovrappone alla X.
  useEffect(() => {
    const openSheet = view !== "territory" || listOpen || !!selected || !!discover || eventsOpen || liveOpen || !!selectedEvent || !!energyCard;
    window.dispatchEvent(new CustomEvent("fuma-map-sheet", { detail: openSheet }));
    return () => window.dispatchEvent(new CustomEvent("fuma-map-sheet", { detail: false }));
  }, [view, listOpen, selected, discover, eventsOpen, liveOpen, selectedEvent, energyCard]);

  const locateMe = () => {
    if (!navigator.geolocation) return toast.error("Geolocalizzazione non disponibile");
    navigator.geolocation.getCurrentPosition(
      (pos) => { setMe({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setFocus({ lat: pos.coords.latitude, lng: pos.coords.longitude, zoom: 16 }); },
      () => toast.error("Impossibile ottenere la posizione"), { enableHighAccuracy: true, timeout: 8000 });
  };
  const resetHome = () => { doFlash(); setSelected(null); setActiveComune(null); setListOpen(false); setView("territory"); setFocus({ lat: ACTIVE_CENTROID[1], lng: ACTIVE_CENTROID[0], zoom: TERRITORY_ZOOM, pitch: TERRITORY_PITCH }); };
  const goHub = (path) => navigate(user ? path : "/login");
  const goMenu = (p) => navigate(`/attivita/${p.id}`);

  const ActionBtn = ({ icon: Icon, title, onClick, badge, testid }) => (
    <button data-testid={testid} onClick={onClick} title={title} aria-label={title}
      className="relative h-11 w-11 rounded-2xl bg-[#0F1016]/85 backdrop-blur-xl border border-white/10 text-white flex items-center justify-center active:scale-95 transition-transform shadow-lg hover:border-[#00E676]/40">
      <Icon className="h-5 w-5" />
      {badge ? <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-[#FF3B30] text-[9px] font-bold flex items-center justify-center">{badge}</span> : null}
    </button>
  );
  void ActionBtn;
  const NavItem = ({ icon: Icon, label, active, onClick, badge, testid }) => (
    <button data-testid={testid} onClick={onClick} className={`relative flex flex-col items-center gap-1 flex-1 py-1 ${active ? "text-[#00E676]" : "text-zinc-400"}`}>
      <Icon className={`h-5 w-5 ${active ? "fill-[#00E676]/20" : ""}`} />
      <span className="text-[10px] font-medium">{label}</span>
      {badge ? <span className="absolute top-0 right-1/2 translate-x-4 h-4 min-w-4 px-1 rounded-full bg-[#FF3B30] text-white text-[9px] font-bold flex items-center justify-center">{badge}</span> : null}
    </button>
  );

  const tierBadge = (tier) =>
    tier === "sponsor" ? <span className="text-[9px] text-[#FFD700] bg-[#FFD700]/15 border border-[#FFD700]/40 rounded px-1.5 py-0.5 inline-flex items-center gap-0.5"><Star className="h-2.5 w-2.5 fill-[#FFD700]" /> Sponsor</span>
    : tier === "premium" ? <span className="text-[9px] text-[#7DF9FF] bg-[#7DF9FF]/15 border border-[#7DF9FF]/40 rounded px-1.5 py-0.5">Premium</span>
    : null;

  const trafficRoutes = useMemo(    () => (ghostRoutes || []).slice(0, 3).map((r) => r.slice(Math.floor(r.length * 0.35), Math.floor(r.length * 0.35) + 30)).filter((r) => r.length >= 2),
    [ghostRoutes]
  );

  return (
    <div className={`explore-shell theme-${theme} h-[100dvh] w-full overflow-hidden relative bg-[#0A0A0C] select-none animate-in fade-in duration-300`} data-testid="explore-shell">
      <MapEngine layers={layers} lines={mapLines} center={ACTIVE_CENTROID_EFF} zoom={TERRITORY_ZOOM} pitch={TERRITORY_PITCH} focus={focus} ghosts={ghostCount} ghostSeeds={ghostSeeds} ghostRoutes={ghostRoutes} trafficRoutes={trafficRoutes} />
      <MapCollectible active={view === "territory"} />

      <div className={`map-veil ${flash ? "on" : ""}`} aria-hidden="true" />
      {burst && (
        <div className="reward-burst" data-testid="reward-burst">
          <div className="reward-burst-card">
            <div className="reward-burst-title">🎉 {burst.tier ? `${burst.tier}!` : "Ricompensa!"}</div>
            <div className="reward-burst-sub">{burst.title}</div>
            <div className="reward-burst-chips">
              {burst.xp > 0 && <span className="reward-chip xp" data-testid="reward-xp">+{burst.xp} XP</span>}
              {burst.credits > 0 && <span className="reward-chip cr" data-testid="reward-credits">+{burst.credits} crediti</span>}
              {burst.badges > 0 && <span className="reward-chip bd" data-testid="reward-badge">🏅 {burst.badges} badge</span>}
            </div>
          </div>
        </div>
      )}      {view !== "territory" && (
        <div className="map-ambient" aria-hidden="true">
          <div className="amb-glow" />
          {PARTICLES.slice(0, PERF.particles).map((p, i) => (
            <span key={i} className="amb-p" style={{ left: p.left, top: p.top, animationDuration: p.dur, animationDelay: p.delay }} />
          ))}
        </div>
      )}

      {/* HUD top */}
      <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-3 z-30 flex flex-col items-center gap-2">
        <button data-testid="home-logo" onClick={resetHome} className="pointer-events-auto flex flex-col items-center leading-none rounded-2xl px-4 py-1.5 bg-[#0F1016]/85 backdrop-blur-xl border border-white/10 shadow-lg active:scale-95 transition-transform">
          <span className="font-['Unbounded'] font-black text-sm tracking-tight">Fuma c'anduma</span>
          <span className="font-['Unbounded'] font-bold text-[8px] tracking-[0.4em] uppercase text-[#00E676]">World</span>
        </button>
        <div className="pointer-events-auto rounded-full bg-[#0F1016]/85 backdrop-blur-xl border border-white/10 shadow-lg">
          <HomeModeToggle current="mappa" />
        </div>
      </div>
      <div className="absolute inset-x-0 top-0 z-20 p-3 pointer-events-none">
        <div className="flex items-start justify-between gap-2">
          <div className="space-y-2 mt-14">
            {view === "territory" ? null : (
              <div className="pointer-events-auto flex items-center gap-2">
                <button data-testid="map-back" onClick={goBack} className="h-10 w-10 shrink-0 rounded-2xl bg-[#0F1016]/85 backdrop-blur-xl border border-white/10 text-white flex items-center justify-center active:scale-95 transition-transform">
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <div data-testid="map-breadcrumb" className="flex items-center gap-1.5 rounded-2xl px-3.5 py-2 bg-[#0F1016]/85 backdrop-blur-xl border border-white/10 shadow-lg text-xs">
                  <button onClick={resetHome} className="text-zinc-400 hover:text-white">Territorio</button>
                  <span className="text-zinc-600">›</span>
                  <span className="font-['Unbounded'] font-bold text-[#00E676]">{activeComune?.name}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Barra statistiche verticale compatta — centro-destra, non copre CTA/chat */}
      <div className="absolute right-3 top-1/2 -translate-y-1/2 z-20">
        <MapStatStrip
          openBusinesses={pois.filter((p) => p.orderable).length}
          experiencesAvailable={stats.experiences_available || 0}
          activeEvents={events.length}
          credits={user ? (user.credits ?? 0) : null}
          levelLabel={user && level ? level.level_label : null}
        />
      </div>

      {/* HUD Sponsor — overlay FISSO (viewport-relative), indipendente dal motore mappa */}
      {view !== "territory" && activeComune && (
        <div className="absolute left-3 top-28 z-20 flex flex-col gap-2 pointer-events-none" data-testid="sponsor-hud">
          {DEMO_SPONSORS.map((s) => (
            <button key={s.id} data-testid={`sponsor-hud-${s.id}`} onClick={() => toast(`✦ ${s.name} — ${s.tag}`)}
              style={{ borderColor: `${s.color}66` }}
              className="pointer-events-auto text-left rounded-2xl bg-[#0F1016]/85 backdrop-blur-xl border px-3 py-2 shadow-lg active:scale-95 transition-transform max-w-[180px]">
              <div className="text-[9px] uppercase tracking-wider font-semibold" style={{ color: s.color }}>{s.tag}</div>
              <div className="text-xs font-semibold text-white truncate">{s.name}</div>
            </button>
          ))}
        </div>
      )}

      {/* Locate buttons (bottom-left) */}
      <div className="absolute left-3 bottom-52 z-20 flex flex-col gap-2">
        <button data-testid="btn-locate" onClick={locateMe} className="h-12 w-12 rounded-full bg-[#0F1016]/85 backdrop-blur-xl border border-white/10 text-[#00E676] flex items-center justify-center active:scale-95 transition-transform"><Crosshair className="h-5 w-5" /></button>
        <button data-testid="btn-home" onClick={resetHome} className="h-12 w-12 rounded-full bg-[#0F1016]/85 backdrop-blur-xl border border-white/10 text-white flex items-center justify-center active:scale-95 transition-transform"><Navigation className="h-5 w-5" /></button>
      </div>

      {/* Hint / ricerca per livello */}
      {view === "territory" && !discover && (
        <div className="absolute left-0 right-0 bottom-[138px] z-20 px-3 pointer-events-none">
          <div className="max-w-2xl mx-auto flex gap-2 overflow-x-auto no-scrollbar pointer-events-auto pb-1" data-testid="suggestion-bar">
            {SUGGESTIONS.map((s) => (
              <button key={s.id} data-testid={`suggestion-${s.id}`} onClick={() => handleSuggestion(s)}
                className="shrink-0 rounded-full pl-3 pr-4 py-2.5 bg-[#0F1016]/85 backdrop-blur-xl border border-white/10 text-sm text-zinc-100 shadow-lg active:scale-95 transition-transform whitespace-nowrap hover:border-[#00E676]/40">
                <span className="mr-1.5">{s.emoji}</span>{s.label}
              </button>
            ))}
          </div>
        </div>
      )}
      {view === "activities" && !listOpen && (
        <div className="absolute left-0 right-0 bottom-[140px] z-20 px-3 flex justify-center pointer-events-none">
          <button data-testid="open-all-activities" onClick={() => setListOpen(true)} className="pointer-events-auto rounded-full pl-4 pr-5 py-2.5 bg-[#00E676] text-black font-semibold text-sm shadow-lg active:scale-95 transition-transform flex items-center gap-2">
            <MapIcon className="h-4 w-4" /> Scopri tutte le attività ({comuneActs.length})
          </button>
        </div>
      )}

      {/* Esperienze AI — pulsante prominente sempre visibile sopra la bottom-nav */}
      <div className="absolute left-0 right-0 bottom-[70px] z-20 px-3 flex justify-center pointer-events-none">
        <button data-testid="hud-experiences" onClick={() => navigate("/esperienze")}
          className="pointer-events-auto group relative w-full max-w-md h-14 rounded-2xl bg-gradient-to-r from-[#7DF9FF] via-[#4ADED0] to-[#00E676] text-black font-bold flex items-center justify-center gap-2.5 shadow-[0_6px_24px_rgba(0,230,118,0.35)] active:scale-[0.98] transition-transform overflow-hidden">
          <span className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity" />
          <Sparkles className="h-6 w-6 relative" />
          <span className="relative text-base tracking-tight">Esperienze AI</span>
          <span className="relative text-[11px] font-semibold bg-black/15 rounded-full px-2 py-0.5">Crea la tua serata ✨</span>
        </button>
      </div>

      {/* Carrello flottante (spostato dalla nav principale per ridurre il carico) */}
      {count > 0 && (
        <div className="absolute right-3 bottom-[132px] z-20">
          <button data-testid="map-cart-fab" onClick={() => navigate("/app/checkout")}
            className="relative flex items-center gap-2 h-12 pl-3.5 pr-4 rounded-full bg-[#00E676] text-black font-semibold shadow-[0_6px_20px_rgba(0,230,118,0.4)] active:scale-95 transition-transform">
            <span className="relative">
              <ShoppingCart className="h-5 w-5" />
              <span className="absolute -top-2 -right-2 h-4 min-w-4 px-1 rounded-full bg-[#FF3B30] text-white text-[10px] font-bold flex items-center justify-center">{count}</span>
            </span>
            <span data-testid="map-cart-total" className="text-sm tabular-nums">€{Number(subtotal || 0).toFixed(2)}</span>
          </button>
        </div>
      )}

      {/* Bottom nav — 5 macro-aree unificate (coerenti con la tab bar cliente) */}
      <div className="absolute inset-x-0 bottom-0 z-20 bg-[#0A0A0C]/92 backdrop-blur-xl border-t border-white/10 px-2 pt-1.5 pb-2 flex">
        <NavItem testid="nav-home" icon={Home} label="Home" onClick={() => navigate("/home")} />
        <NavItem testid="nav-mappa" icon={MapIcon} label="Mappa" active onClick={resetHome} />
        <NavItem testid="nav-scopri" icon={Compass} label="Scopri" onClick={() => navigate("/app/scopri")} />
        <NavItem testid="nav-attivita" icon={ClipboardList} label="Attività" onClick={() => navigate(user ? "/app/attivita" : "/login")} />
        <NavItem testid="nav-profilo" icon={User} label="Profilo" onClick={() => navigate(user ? "/app/profile" : "/login")} />
      </div>

      {/* Discovery: risultati filtrati da un suggerimento */}
      {discover && (
        <div className="events-panel" data-testid="discover-panel">
          <div className="events-panel-inner no-scrollbar">
            <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-white/20" />
            <button data-testid="discover-close" onClick={() => setDiscover(null)} className="absolute right-6 top-6 text-zinc-400 hover:text-white"><X className="h-5 w-5" /></button>
            <h3 className="font-['Unbounded'] font-bold text-lg">{discover.label}</h3>
            <p className="text-xs text-zinc-500 mt-1 mb-3">{discover.items.length} risultati trovati</p>
            <div className="space-y-2">
              {discover.items.map((p) => (
                <button key={p.id} data-testid={`discover-item-${p.id}`} onClick={() => { setDiscover(null); openPoi(p); }} className="w-full text-left flex gap-3 rounded-2xl p-2.5 bg-[#131316] border border-white/10 hover:border-[#00E676]/40 transition-colors">
                  <div className="h-14 w-14 shrink-0 rounded-xl overflow-hidden bg-[#1C1C1F] flex items-center justify-center border border-white/10">
                    {p.image ? <img src={p.image} alt={p.name} className="h-full w-full object-cover" /> : <span className="h-6 w-6 [&>svg]:h-full [&>svg]:w-full"><CategoryGlyph category={p.category} /></span>}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm truncate">{p.name}</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">{CATEGORY_LABELS[categoryKey(p.category)]} · {p.orderable ? "Aperto" : "In arrivo"}</div>
                    {p.has_offer && <div className="text-[11px] text-[#FFD700] mt-0.5 truncate">✦ {p.offer_title}</div>}
                  </div>
                </button>
              ))}
              {discover.items.length === 0 && <div className="text-zinc-500 py-10 text-center text-sm">Nessun risultato al momento. Riprova più tardi!</div>}
            </div>
          </div>
        </div>
      )}

      {/* Scopri tutte le attività del comune (In evidenza = Premium/Sponsor sulla mappa) */}
      {view === "activities" && listOpen && activeComune && (
        <div className="events-panel" data-testid="all-activities-panel">
          <div className="events-panel-inner no-scrollbar">
            <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-white/20" />
            <button data-testid="all-activities-close" onClick={() => setListOpen(false)} className="absolute right-6 top-6 text-zinc-400 hover:text-white"><X className="h-5 w-5" /></button>
            <h3 className="font-['Unbounded'] font-bold text-lg flex items-center gap-2"><MapIcon className="h-5 w-5 text-[#00E676]" /> {activeComune.name}</h3>
            <p className="text-xs text-zinc-500 mt-1 mb-3">In evidenza sulla mappa le attività <span className="text-[#7DF9FF]">Premium</span> e <span className="text-[#FFD700]">Sponsor</span>. Qui trovi tutte le attività del comune.</p>
            <div className="flex gap-2 overflow-x-auto no-scrollbar pb-3 -mx-1 px-1" data-testid="hub-filters">
              {[{ k: "all", l: "Tutte" }, { k: "evidenza", l: "In evidenza" }, { k: "aperti", l: "Aperti ora" }, { k: "mangiare", l: "Mangiare" }, { k: "servizi", l: "Servizi" }].map((c) => (
                <button key={c.k} data-testid={`hub-filter-${c.k}`} onClick={() => setHubFilter(c.k)}
                  className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-semibold border transition-colors ${hubFilter === c.k ? "bg-[#00E676] text-black border-[#00E676]" : "bg-[#131316] text-zinc-300 border-white/10 hover:border-[#00E676]/40"}`}>{c.l}</button>
              ))}
              <button data-testid="hub-filter-eventi" onClick={() => { setListOpen(false); setEventsOpen(true); }} className="shrink-0 rounded-full px-3.5 py-1.5 text-xs font-semibold border bg-[#131316] text-zinc-300 border-white/10 hover:border-[#00E676]/40">Eventi</button>
              <button data-testid="hub-filter-esperienze" onClick={() => navigate("/esperienze")} className="shrink-0 rounded-full px-3.5 py-1.5 text-xs font-semibold border bg-[#131316] text-zinc-300 border-white/10 hover:border-[#00E676]/40">Esperienze</button>
            </div>
            <div className="relative mb-3">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
              <Input data-testid="all-activities-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca attività…" className="pl-10 h-11 rounded-full bg-[#131316] border-white/10" />
            </div>
            <div className="space-y-2">
              {listActs.map((p) => (
                <button key={p.id} data-testid={`list-activity-${p.id}`} onClick={() => { setListOpen(false); openPoi(p); }} className="w-full text-left flex gap-3 rounded-2xl p-2.5 bg-[#131316] border border-white/10 hover:border-[#00E676]/40 transition-colors">
                  <div className="h-14 w-14 shrink-0 rounded-xl overflow-hidden bg-[#1C1C1F] flex items-center justify-center border border-white/10">
                    {p.image ? <img src={p.image} alt={p.name} className="h-full w-full object-cover" /> : <span className="h-6 w-6 [&>svg]:h-full [&>svg]:w-full"><CategoryGlyph category={p.category} /></span>}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap"><span className="font-semibold text-sm truncate">{p.name}</span>{tierBadge(p.tier)}</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">{CATEGORY_LABELS[categoryKey(p.category)]} · {p.orderable ? "Aperto" : "In arrivo"}</div>
                    {p.has_offer && <div className="text-[11px] text-[#FFD700] mt-0.5 truncate">✦ {p.offer_title}</div>}
                  </div>
                </button>
              ))}
              {listActs.length === 0 && <div className="text-zinc-500 py-10 text-center text-sm">Nessuna attività in questo comune.</div>}
            </div>
          </div>
        </div>
      )}

      {/* Eventi in evidenza (pannello) */}
      {eventsOpen && (
        <div className="events-panel" data-testid="events-panel">
          <div className="events-panel-inner no-scrollbar">
            <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-white/20" />
            <button data-testid="events-panel-close" onClick={() => setEventsOpen(false)} className="absolute right-6 top-6 text-zinc-400 hover:text-white"><X className="h-5 w-5" /></button>
            <h3 className="font-['Unbounded'] font-bold text-lg flex items-center gap-2"><CalendarDays className="h-5 w-5 text-[#00E676]" /> Eventi nel Canavese</h3>
            <p className="text-xs text-zinc-500 mt-1 mb-4">Sagre, feste, concerti e molto altro sul territorio.</p>
            <div className="space-y-3">
              {events.map((e) => (
                <button key={e.id} data-testid={`event-item-${e.id}`} onClick={() => openEvent(e)} className="w-full text-left flex gap-3 rounded-2xl p-2.5 bg-[#131316] border border-white/10 hover:border-[#00E676]/40 transition-colors">
                  <div className="h-16 w-16 shrink-0 rounded-xl overflow-hidden bg-[#1C1C1F] flex items-center justify-center border border-white/10">
                    {e.image ? <img src={e.image} alt={e.title} className="h-full w-full object-cover" /> : <CalendarDays className="h-6 w-6 text-white/30" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-semibold text-sm truncate">{e.title}</span>
                      {e.sponsored && <span className="text-[9px] text-fuchsia-300 bg-fuchsia-500/15 border border-fuchsia-500/30 rounded px-1.5 py-0.5 flex items-center gap-0.5"><Megaphone className="h-2.5 w-2.5" /> Sponsor</span>}
                      {!e.sponsored && e.featured && <span className="text-[9px] text-amber-300 bg-amber-400/15 border border-amber-400/30 rounded px-1.5 py-0.5 flex items-center gap-0.5"><Star className="h-2.5 w-2.5" /> Evidenza</span>}
                    </div>
                    <div className="text-[11px] text-zinc-400 mt-0.5">{EVENT_CAT_LABELS[e.category] || "Evento"} · {e.comune || e.location_name || ""}</div>
                    <div className="text-[11px] text-[#00E676] mt-0.5">{e.is_live ? "In corso ora" : (e.start_at ? new Date(e.start_at).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "")}</div>
                  </div>
                </button>
              ))}
              {events.length === 0 && <div className="text-zinc-500 py-10 text-center text-sm">Nessun evento in programma al momento.</div>}
            </div>
          </div>
        </div>
      )}

      {/* Mini-card "Energia Fuma": dettagli del comune solo al tap */}
      {energyCard && (
        <div className="absolute inset-x-0 bottom-[60px] z-40 px-3 animate-[slideUp_0.28s_ease-out]" data-testid="energy-card">
          <div className="relative mx-auto max-w-sm bg-[#0A0A0C]/95 backdrop-blur-xl border border-white/12 rounded-3xl p-5 shadow-[0_-8px_40px_rgba(0,0,0,0.6)]">
            <button data-testid="energy-card-close" onClick={() => setEnergyCard(null)} className="absolute right-4 top-4 text-zinc-400 hover:text-white"><X className="h-5 w-5" /></button>
            <div className="flex items-center gap-2.5">
              <span className={`h-3 w-3 rounded-full ${energyCard.partner ? "bg-[#FFD700]" : energyCard.active ? "bg-[#00E676]" : "bg-zinc-500"}`} style={{ boxShadow: energyCard.active ? "0 0 10px currentColor" : "none" }} />
              <h3 className="font-['Unbounded'] font-bold text-xl">{energyCard.name}</h3>
              {energyCard.live > 0 && <span className="text-[10px] font-bold text-[#7DF9FF] bg-[#7DF9FF]/15 border border-[#7DF9FF]/40 rounded-full px-2 py-0.5">LIVE</span>}
            </div>
            {energyCard.active ? (
              <>
                <div className="mt-3 text-sm text-white/85">
                  <span className="text-[#00E676] font-semibold">{energyCard.count}</span> attività
                  {energyCard.openNow > 0 && <> · <span className="text-[#7DF9FF] font-semibold">{energyCard.openNow}</span> aperte ora</>}
                </div>
                {(energyCard.events > 0 || energyCard.experiences > 0) && (
                  <div className="mt-1 text-sm text-white/55">
                    {energyCard.events > 0 && <>{energyCard.events} event{energyCard.events === 1 ? "o" : "i"}</>}
                    {energyCard.events > 0 && energyCard.experiences > 0 && <> · </>}
                    {energyCard.experiences > 0 && <>{energyCard.experiences} esperienze</>}
                  </div>
                )}
                {energyCard.liveOrders > 0 && (
                  <div className="mt-1 text-sm text-[#FF9F45] font-medium">🛵 {energyCard.liveOrders} consegn{energyCard.liveOrders === 1 ? "a" : "e"} in corso ora</div>
                )}
                <button data-testid="energy-card-explore" onClick={() => enterComune(energyCard)} className="mt-4 w-full rounded-2xl bg-[#00E676] text-black font-semibold py-3 active:scale-95 transition-transform">Esplora {energyCard.name}</button>
              </>
            ) : (
              <>
                <div className="mt-3 text-sm text-white/70">
                  {energyCard.unlock ? (energyCard.unlock.remaining > 0 ? `Mancano ${energyCard.unlock.remaining} attività per accendere questo comune.` : "Sta per accendersi!") : "Presto disponibile."}
                </div>
                <button data-testid="energy-card-propose" onClick={() => { setEnergyCard(null); navigate("/app/regala"); }} className="mt-4 w-full rounded-2xl bg-[#FFD700] text-black font-semibold py-3 active:scale-95 transition-transform">Proponi un'attività</button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Canavese Live (Fase D) */}
      {liveOpen && (
        <div className="events-panel" data-testid="live-panel">
          <div className="events-panel-inner no-scrollbar">
            <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-white/20" />
            <button data-testid="live-panel-close" onClick={() => setLiveOpen(false)} className="absolute right-6 top-6 text-zinc-400 hover:text-white"><X className="h-5 w-5" /></button>
            <h3 className="font-['Unbounded'] font-bold text-lg flex items-center gap-2"><Radio className="h-5 w-5 text-[#00E676]" /> Canavese Live</h3>
            <p className="text-xs text-zinc-500 mt-1 mb-4">Cosa succede ora sul territorio: offerte lampo, eventi, nuove attività.</p>
            {liveFeed ? (
              <div className="space-y-5">
                {liveFeed.flash_offers?.length > 0 && (
                  <div>
                    <div className="text-[11px] uppercase tracking-widest text-red-300 mb-2 flex items-center gap-1"><Megaphone className="h-3.5 w-3.5" /> Offerte lampo</div>
                    <div className="space-y-2">
                      {liveFeed.flash_offers.map((o) => (
                        <button key={o.id} data-testid={`live-offer-${o.id}`} onClick={() => navigate(`/attivita/${o.id}`)} className="w-full text-left flex gap-3 rounded-2xl p-2.5 bg-[#131316] border border-red-500/25 hover:border-red-500/50 transition-colors">
                          <div className="h-14 w-14 shrink-0 rounded-xl overflow-hidden bg-[#1C1C1F] flex items-center justify-center border border-white/10">{o.image ? <img src={o.image} alt={o.name} className="h-full w-full object-cover" /> : <MapPin className="h-5 w-5 text-white/30" />}</div>
                          <div className="min-w-0 flex-1"><div className="font-semibold text-sm truncate">{o.name}</div><div className="text-[11px] text-red-300 mt-0.5 truncate">{o.offer_title}</div><div className="text-[11px] text-zinc-500">{o.category} · {o.zone}</div></div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {liveFeed.events?.length > 0 && (
                  <div>
                    <div className="text-[11px] uppercase tracking-widest text-[#00E676] mb-2 flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> Eventi</div>
                    <div className="space-y-2">
                      {liveFeed.events.map((e) => (
                        <button key={e.id} data-testid={`live-event-${e.id}`} onClick={() => { setLiveOpen(false); openEvent(e); }} className="w-full text-left flex gap-3 rounded-2xl p-2.5 bg-[#131316] border border-white/10 hover:border-[#00E676]/40 transition-colors">
                          <div className="h-14 w-14 shrink-0 rounded-xl overflow-hidden bg-[#1C1C1F] flex items-center justify-center border border-white/10">{e.image ? <img src={e.image} alt={e.title} className="h-full w-full object-cover" /> : <CalendarDays className="h-5 w-5 text-white/30" />}</div>
                          <div className="min-w-0 flex-1"><div className="font-semibold text-sm truncate">{e.title}</div><div className="text-[11px] text-zinc-500 mt-0.5">{EVENT_CAT_LABELS[e.category] || "Evento"} · {e.comune}</div><div className="text-[11px] text-[#00E676] mt-0.5">{e.is_live ? "In corso ora" : (e.start_at ? new Date(e.start_at).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "")}</div></div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {liveFeed.new_businesses?.length > 0 && (
                  <div>
                    <div className="text-[11px] uppercase tracking-widest text-cyan-300 mb-2 flex items-center gap-1"><Star className="h-3.5 w-3.5" /> Nuove attività</div>
                    <div className="space-y-2">
                      {liveFeed.new_businesses.map((b) => (
                        <button key={b.id} data-testid={`live-new-${b.id}`} onClick={() => navigate(`/attivita/${b.id}`)} className="w-full text-left flex gap-3 rounded-2xl p-2.5 bg-[#131316] border border-white/10 hover:border-cyan-400/40 transition-colors">
                          <div className="h-14 w-14 shrink-0 rounded-xl overflow-hidden bg-[#1C1C1F] flex items-center justify-center border border-white/10">{b.image ? <img src={b.image} alt={b.name} className="h-full w-full object-cover" /> : <MapPin className="h-5 w-5 text-white/30" />}</div>
                          <div className="min-w-0 flex-1"><div className="font-semibold text-sm truncate">{b.name} <span className="text-[9px] text-cyan-300 bg-cyan-500/15 border border-cyan-500/30 rounded px-1.5 py-0.5 ml-1">Nuova</span></div><div className="text-[11px] text-zinc-500 mt-0.5">{b.category} · {b.zone}</div></div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {liveFeed.challenges?.length > 0 && (
                  <div>
                    <div className="text-[11px] uppercase tracking-widest text-amber-300 mb-2 flex items-center gap-1"><Gift className="h-3.5 w-3.5" /> Sfide del momento</div>
                    <div className="space-y-2">
                      {liveFeed.challenges.map((c) => (
                        <div key={c.code} data-testid={`live-challenge-${c.code}`} className="rounded-2xl p-2.5 bg-[#131316] border border-amber-400/20"><div className="font-semibold text-sm">{c.title}</div><div className="text-[11px] text-zinc-500 mt-0.5">{c.description}</div></div>
                      ))}
                    </div>
                  </div>
                )}
                {(liveFeed.count === 0) && ((liveFeed.challenges?.length || 0) === 0) && <div className="text-zinc-500 py-10 text-center text-sm">Nessuna novità in questo momento. Torna presto!</div>}
              </div>
            ) : <div className="text-zinc-500 py-10 text-center text-sm">Caricamento…</div>}
          </div>
        </div>
      )}

      {/* Dettaglio evento */}
      {selectedEvent && (
        <div data-testid="event-sheet" className="absolute inset-x-0 bottom-[60px] z-30 animate-[slideUp_0.28s_ease-out]">
          <div className="mx-auto max-w-lg bg-[#0A0A0C] border-t border-white/15 rounded-t-[28px] p-5 pb-6 shadow-[0_-8px_40px_rgba(0,0,0,0.6)]">
            <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-white/20" />
            <button data-testid="event-sheet-close" onClick={() => setSelectedEvent(null)} className="absolute right-5 top-5 text-zinc-400 hover:text-white"><X className="h-5 w-5" /></button>
            {selectedEvent.image && <img src={selectedEvent.image} alt={selectedEvent.title} className="h-36 w-full object-cover rounded-2xl mb-4 border border-white/10" />}
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-['Unbounded'] font-bold text-lg">{selectedEvent.title}</h3>
              {selectedEvent.is_live && <span className="text-[11px] font-semibold rounded-full px-2 py-0.5 border bg-[#00E676]/15 text-[#00E676] border-[#00E676]/30">In corso</span>}
            </div>
            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
              <span className="text-[11px] rounded-full bg-white/10 px-2 py-0.5 text-zinc-200">{EVENT_CAT_LABELS[selectedEvent.category] || "Evento"}</span>
              {selectedEvent.sponsored && <span className="text-[11px] text-fuchsia-300 bg-fuchsia-500/15 border border-fuchsia-500/30 rounded-full px-2 py-0.5 flex items-center gap-1"><Megaphone className="h-3 w-3" /> {selectedEvent.sponsor_name || "Sponsorizzato"}</span>}
            </div>
            {(selectedEvent.location_name || selectedEvent.comune) && <div className="flex items-center gap-1.5 text-sm text-zinc-300 mt-3"><MapPin className="h-4 w-4 text-[#00E676]" /> {[selectedEvent.location_name, selectedEvent.comune].filter(Boolean).join(", ")}</div>}
            <div className="text-sm text-zinc-400 mt-1.5">{selectedEvent.start_at ? new Date(selectedEvent.start_at).toLocaleString("it-IT", { weekday: "short", day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" }) : ""}{selectedEvent.end_at ? ` → ${new Date(selectedEvent.end_at).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""}</div>
            {selectedEvent.description && <p className="mt-3 text-sm text-zinc-400">{selectedEvent.description}</p>}
          </div>
        </div>
      )}

      {/* Bottom sheet anteprima */}
      {selected && (
        <div data-testid="poi-sheet" className="absolute inset-x-0 bottom-[60px] z-30 animate-[slideUp_0.28s_ease-out]">
          <div className="mx-auto max-w-lg bg-[#0A0A0C] border-t border-white/15 rounded-t-[28px] p-5 pb-6 shadow-[0_-8px_40px_rgba(0,0,0,0.6)]">
            <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-white/20" />
            <button data-testid="sheet-close" onClick={() => setSelected(null)} className="absolute right-5 top-5 text-zinc-400 hover:text-white"><X className="h-5 w-5" /></button>
            <div className="flex gap-4">
              <div className="h-20 w-20 shrink-0 rounded-2xl overflow-hidden bg-[#1C1C1F] flex items-center justify-center border border-white/10">
                {selected.image ? <img src={selected.image} alt={selected.name} className="h-full w-full object-cover" /> : <span className="h-9 w-9 [&>svg]:h-full [&>svg]:w-full"><CategoryGlyph category={selected.category} /></span>}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-['Unbounded'] font-bold text-lg truncate">{selected.name}</h3>
                <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                  <span data-testid="sheet-status" className={`text-[11px] font-semibold rounded-full px-2 py-0.5 border ${selected.orderable ? "bg-[#00E676]/15 text-[#00E676] border-[#00E676]/30" : "bg-zinc-500/15 text-zinc-400 border-zinc-500/30"}`}>{selected.orderable ? "Aperto" : "In arrivo"}</span>
                  {selected.rating > 0 && <span className="flex items-center gap-0.5 text-[11px] text-amber-300"><Star className="h-3 w-3 fill-amber-300" /> {selected.rating}</span>}
                  <span className="flex items-center gap-1 text-[11px] text-zinc-300"><Bike className="h-3 w-3" /> {selected.prep_time || 25} min</span>
                  {selected.price_range && <span className="text-[11px] text-zinc-300 font-medium">{selected.price_range}</span>}
                  <span className="text-[11px] rounded-full bg-white/10 px-2 py-0.5 text-zinc-200">{CATEGORY_LABELS[categoryKey(selected.category)]}</span>
                </div>
                {selected.zone && <div className="text-[11px] text-zinc-500 mt-1">{selected.zone}</div>}
              </div>
            </div>
            {selected.has_offer && <div data-testid="sheet-offer" className="mt-4 rounded-xl border border-[#FFD700]/40 bg-[#FFD700]/10 text-[#FFD700] px-3 py-2 text-sm font-medium">✦ {selected.offer_title}</div>}
            {selected.description && <p className="mt-3 text-sm text-zinc-400 line-clamp-2">{selected.description}</p>}
            <button data-testid="sheet-cta" onClick={() => goMenu(selected)} className="mt-5 w-full h-12 rounded-full bg-[#00E676] text-black hover:bg-[#00C853] font-semibold text-base shadow-[0_4px_0_0_#00C853] active:translate-y-0.5 transition-transform">
              {selected.orderable ? "Vedi menu e ordina" : "Scopri l'attività"}
            </button>
          </div>
        </div>
      )}

      {/* Piano Fuma AI — tappe numerate + percorso (arrivo dalla Home AI) */}
      {plan && (
        <div className="events-panel" data-testid="fuma-plan-panel">
          <div className="events-panel-inner no-scrollbar">
            <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-white/20" />
            <button data-testid="fuma-plan-close" onClick={() => { try { sessionStorage.removeItem("fuma_active_plan"); } catch (e) {} setPlan(null); window.history.replaceState({}, "", "/esplora"); }} className="absolute right-6 top-6 text-zinc-400 hover:text-white"><X className="h-5 w-5" /></button>
            <h3 className="font-['Unbounded'] font-bold text-lg flex items-center gap-2"><Sparkles className="h-5 w-5 text-[#00E676]" /> {plan.title}</h3>
            <div className="flex items-center gap-3 text-[11px] text-zinc-400 mt-1 mb-3 flex-wrap">
              {plan.start_time && <span>🕒 {plan.start_time}</span>}
              {plan.total_distance_km != null && <span>🧭 {plan.total_distance_km} km</span>}
              {plan.total_price > 0 && <span>€ {plan.total_price.toFixed(2)}</span>}
            </div>
            <div className="space-y-2">
              {plan.steps.map((s, i) => (
                <button key={i} data-testid={`fuma-plan-step-${i}`} onClick={() => s.business_id && navigate(`/attivita/${s.business_id}`)} className="w-full text-left flex items-center gap-3 rounded-2xl p-2.5 bg-[#131316] border border-white/10 hover:border-[#00E676]/40 transition-colors">
                  <div className="h-8 w-8 rounded-full bg-[#00E676]/15 text-[#00E676] flex items-center justify-center text-sm font-bold shrink-0">{i + 1}</div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm truncate">{s.label}</div>
                    <div className="text-[11px] text-zinc-500 truncate">{s.business_name} · {s.time}{s.lat == null ? " · posizione n/d" : ""}</div>
                  </div>
                  {s.price > 0 && <div className="text-xs text-zinc-400">€{s.price.toFixed(2)}</div>}
                  <ChevronLeft className="h-4 w-4 text-zinc-600 rotate-180 shrink-0" />
                </button>
              ))}
            </div>
            <p className="text-[11px] text-zinc-500 mt-3">Le tappe sono numerate sulla mappa e collegate dal percorso. Tocca una tappa per aprire l'attività, ordinare o prenotare.</p>
          </div>
        </div>
      )}

      {/* Attività RADAR (non ancora partner) — richiesta cliente */}
      {radarSel && (
        <div className="events-panel" data-testid="radar-cta-panel">
          <div className="events-panel-inner no-scrollbar">
            <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-white/20" />
            <button data-testid="radar-cta-close" onClick={() => setRadarSel(null)} className="absolute right-6 top-6 text-zinc-400 hover:text-white"><X className="h-5 w-5" /></button>
            <span className="inline-block text-[11px] font-semibold rounded-full px-2 py-0.5 bg-zinc-500/15 text-zinc-400 border border-zinc-500/30">Non ancora su Fuma</span>
            <h3 className="font-['Unbounded'] font-bold text-lg mt-2">{radarSel.name}</h3>
            <div className="text-xs text-zinc-500">{radarSel.category}{radarSel.city ? ` · ${radarSel.city}` : ""}</div>
            <p className="text-sm text-zinc-400 mt-3">Questa attività non è ancora su Fuma. Aiutaci a portarla qui! 💚</p>
            {radarSel.requests > 0 && <div className="text-sm text-[#F59E0B] mt-1">🔥 {radarSel.requests} persone la vogliono su Fuma</div>}
            <button data-testid="radar-want" onClick={() => requestRadar(radarSel.id)} disabled={myReq.includes(radarSel.id)} className="mt-4 w-full rounded-full bg-[#00E676] text-black font-semibold py-2.5 text-sm disabled:opacity-60">{myReq.includes(radarSel.id) ? "❤️ Già richiesta" : "❤️ La voglio su Fuma"}</button>
          </div>
        </div>
      )}
    </div>
  );
}
